import { useState, useEffect } from "react";

const API_BASE = "https://app.carthub.cloud/api";
const DASHBOARD_URL = "https://dashboard.carthub.cloud";

const api = async (endpoint, options = {}) => {
  const token = localStorage.getItem("carthub_agency_token");
  const res = await fetch(`${API_BASE}${endpoint}`, {
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    ...options,
  });
  if (res.status === 401 && token) { const _p = new URLSearchParams(window.location.search); if (!_p.get("checkout")) { localStorage.removeItem("carthub_agency_token"); localStorage.removeItem("carthub_agency"); window.location.href = "/"; } return {}; }
  if (res.status === 429) return res.json();
  return res.json();
};

// ─────────────────────────────────────────────────────
// CSS — allineato a dashboard merchant (shared.js)
// ─────────────────────────────────────────────────────
const styles = `
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --font: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  --text:          #1a1a1a;
  --text2:         #6b7280;
  --text3:         #9ca3af;
  --border:        rgba(26, 26, 26, 0.08);
  --border2:       rgba(26, 26, 26, 0.12);
  --canvas:        #FBFDF7;
  --surface-panel: #ffffff;
  --surface-bright:#ffffff;
  --bg:            var(--canvas);
  --surface:       var(--surface-bright);
  --radius-module: 18px;
  --gap-module:    8px;
  --shadow-module: 0 5px 10px rgba(26, 26, 26, 0.05);
  --shadow-drawer: 0 5px 10px rgba(26, 26, 26, 0.05);
  --accent:        #0A85CF;
  --accent-hover:  #0878b8;
  --link:          #0A85CF;
  --accent-bg:     #e8f4fc;
  --accent-border: #b8daf2;
  --accent-glow:   rgba(10,133,207,0.18);
  --green:         #2e7d32;
  --green-bg:      #f0faf0;
  --green-border:  #c8e6c9;
  --red:           #c62828;
  --red-bg:        #fff5f5;
  --red-border:    #ffcdd2;
  --yellow:        #e65100;
  --yellow-bg:     #fff8f0;
  --yellow-border: #ffe0b2;
}

html, body, #root {
  height: 100%;
  font-family: var(--font);
  font-size: 15px;
  color: var(--text);
  background: var(--bg);
  -webkit-font-smoothing: antialiased;
}

::-webkit-scrollbar { width: 4px; height: 4px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }

.app-loading-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  gap: 16px;
  background: var(--canvas);
  color: var(--text2);
  font-size: 15px;
  font-weight: 500;
  font-family: var(--font);
}
.app-loading-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid rgba(26, 26, 26, 0.08);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: ch-spin 0.8s linear infinite;
}
@keyframes ch-spin { to { transform: rotate(360deg); } }

.app { display: flex; height: 100vh; overflow: hidden; background: var(--canvas); box-sizing: border-box; }

.sidebar-backdrop {
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(26, 26, 26, 0.28);
  z-index: 180;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
}
.sidebar-backdrop.is-open {
  display: block;
  opacity: 1;
  pointer-events: auto;
}

.mobile-nav-toggle {
  display: none;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  margin-right: 10px;
  margin-left: -4px;
  border: none;
  border-radius: 12px;
  background: var(--surface-bright);
  color: var(--text);
  cursor: pointer;
  box-shadow: var(--shadow-module);
  flex-shrink: 0;
  transition: color 0.15s, box-shadow 0.15s;
}
.mobile-nav-toggle:hover { color: var(--accent); }

/* SIDEBAR */
.sidebar {
  width: 290px;
  margin: var(--gap-module);
  background: var(--surface-panel);
  border: none;
  border-radius: var(--radius-module);
  box-shadow: var(--shadow-module);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  overflow-x: hidden;
  overflow-y: auto;
}
.sidebar-head {
  padding: 18px 16px 14px;
  border-bottom: 1px solid rgba(26, 26, 26, 0.06);
}
.sidebar-logo { font-size: 19px; font-weight: 700; letter-spacing: -0.02em; color: var(--text); }
.sidebar-logo span { color: var(--accent); }
.sidebar-sub { font-size: 13px; color: var(--text3); margin-top: 2px; }
.sidebar-nav { padding: 10px 8px; flex: 1; }
.nav-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  color: var(--text2);
  font-size: 15px;
  font-weight: 500;
  transition: all .15s;
  margin-bottom: 1px;
  border: 1px solid transparent;
  user-select: none;
}
.nav-item:hover { background: var(--bg); color: var(--text); }
.nav-item.active { background: var(--accent-bg); color: var(--accent); border-color: var(--accent-border); box-shadow: none; }
.nav-item svg { width: 15px; height: 15px; flex-shrink: 0; }
.nav-badge { margin-left: auto; background: var(--accent); color: #fff; font-size: 13px; font-weight: 600; padding: 1px 6px; border-radius: 20px; }
.sidebar-foot { padding: 12px; border-top: 1px solid rgba(26, 26, 26, 0.06); }
.agency-row { display: flex; align-items: center; gap: 10px; }
.agency-av {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: var(--accent-bg);
  border: 1px solid var(--accent-border);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  color: var(--accent);
  flex-shrink: 0;
}
.agency-name { font-size: 13px; font-weight: 500; color: var(--text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.agency-email { font-size: 13px; color: var(--text3); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.logout-btn { margin-left: auto; background: none; border: none; cursor: pointer; color: var(--text3); padding: 4px; border-radius: 4px; transition: color .15s; flex-shrink: 0; }
.logout-btn:hover { color: var(--red); }

/* MAIN */
.main {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--canvas);
  min-width: 0;
}
.topbar {
  min-height: 56px;
  margin: var(--gap-module);
  background: var(--surface-panel);
  border: none;
  border-radius: var(--radius-module);
  box-shadow: var(--shadow-module);
  display: flex;
  align-items: center;
  padding: 0 20px;
  gap: 10px;
  flex-shrink: 0;
  flex-wrap: wrap;
}
.topbar-title { font-size: 15px; font-weight: 600; flex: 1; min-width: 0; }
.content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: var(--gap-module);
  min-width: 0;
}

/* SLOTS BAR + POOL */
.slots-stack {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: var(--gap-module);
}
.slots-bar {
  background: var(--surface-panel);
  border: none;
  border-radius: var(--radius-module);
  padding: 16px 20px;
  margin-bottom: 0;
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
  box-shadow: var(--shadow-module);
}
.cart-pool-bar { border: 1px solid var(--accent-border); background: linear-gradient(135deg, var(--surface-panel) 0%, var(--accent-bg) 100%); }
.slots-label { font-size: 13px; font-weight: 500; color: var(--text2); white-space: nowrap; }
.slots-count { font-size: 15px; font-weight: 600; color: var(--text); white-space: nowrap; }
.slots-count span { color: var(--accent); }
.slots-progress { flex: 1; height: 6px; background: rgba(26,26,26,0.06); border-radius: 3px; overflow: hidden; min-width: 80px; }
.slots-fill { height: 100%; border-radius: 3px; background: var(--accent); transition: width .3s; }
.slots-fill.warning { background: var(--yellow); }
.slots-fill.full { background: var(--red); }
.trial-badge { font-size: 13px; font-weight: 500; padding: 3px 10px; border-radius: 20px; color: var(--yellow); background: var(--yellow-bg); border: 1px solid var(--yellow-border); white-space: nowrap; }

/* CARDS */
.ch-card {
  background: var(--surface-panel);
  border: none;
  border-radius: var(--radius-module);
  padding: 20px 22px;
  margin-bottom: var(--gap-module);
  box-shadow: var(--shadow-module);
}
.ch-card h2 { font-size: 15px; font-weight: 600; color: #1a1a1a; margin: 0 0 6px 0; }
.ch-card p { font-size: 15px; color: #6b7280; margin: 0 0 20px 0; line-height: 1.6; }
.card-head { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px 12px; margin-bottom: 12px; }
.card-head h2 { margin: 0; }

/* BUTTONS */
.ch-btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; font-size: 15px; font-weight: 500; border-radius: 6px; border: 1px solid #e5e5e5; background: #ffffff; color: #1a1a1a; cursor: pointer; transition: all .15s; font-family: var(--font); white-space: nowrap; }
.ch-btn:hover { background: #f9f9f9; border-color: #d1d1d1; }
.ch-btn-primary { background: #1a1a1a; color: #ffffff; border-color: #1a1a1a; }
.ch-btn-primary:hover { background: #333; border-color: #333; }
.ch-btn-sm { padding: 5px 10px; font-size: 13px; }
.ch-btn:disabled { opacity: .5; cursor: not-allowed; }
.ch-btn-danger { background: var(--red-bg); color: var(--red); border-color: var(--red-border); }
.ch-btn-danger:hover { background: #ffcdd2; }

/* FORM */
.ch-field { margin-bottom: 20px; }
.ch-field label { display: block; font-size: 15px; font-weight: 500; color: #1a1a1a; margin-bottom: 6px; }
.ch-input, .ch-select {
  width: 100%;
  max-width: 480px;
  padding: 8px 12px;
  font-size: 15px;
  font-family: var(--font);
  border: 1px solid rgba(26, 26, 26, 0.08);
  border-radius: 12px;
  color: #1a1a1a;
  background: var(--surface-bright);
  transition: border-color .15s;
  outline: none;
}
.ch-input:focus, .ch-select:focus { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-glow); }
.ch-input.full, .ch-select.full { max-width: 100%; }
.ch-hint { font-size: 13px; color: #6b7280; margin-top: 6px; }

/* STATUS */
.ch-status { font-size: 13px; font-weight: 500; padding: 3px 10px; border-radius: 20px; display: inline-flex; align-items: center; gap: 5px; white-space: nowrap; }
.ch-status.success { color: #2e7d32; background: #f0faf0; border: 1px solid #c8e6c9; }
.ch-status.error   { color: #c62828; background: #fff5f5; border: 1px solid #ffcdd2; }
.ch-status.warning { color: #e65100; background: #fff8f0; border: 1px solid #ffe0b2; }
.ch-status.neutral { color: #6b7280; background: #f5f5f5; border: 1px solid #e5e5e5; }
.ch-status.info    { color: var(--accent); background: var(--accent-bg); border: 1px solid var(--accent-border); }
.status-dot { width: 5px; height: 5px; border-radius: 50%; background: currentColor; }

/* INFO BOX */
.ch-info { background: var(--accent-bg); border: 1px solid var(--accent-border); border-radius: 6px; padding: 12px 16px; font-size: 15px; color: #6b7280; margin-bottom: 20px; line-height: 1.6; }
.ch-info strong { color: #1a1a1a; }
.ch-err { background: #fff5f5; border: 1px solid #ffcdd2; border-radius: 6px; padding: 12px 16px; font-size: 13px; color: #c62828; margin-bottom: 16px; }

/* TABLE */
.table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; width: 100%; max-width: 100%; }
table { width: 100%; border-collapse: collapse; font-size: 15px; }
thead th { padding: 8px 14px; text-align: left; font-size: 13px; font-weight: 600; color: #9ca3af; text-transform: uppercase; letter-spacing: .05em; border-bottom: 1px solid #e5e5e5; background: #f9fafb; white-space: nowrap; }
tbody tr { border-bottom: 1px solid #f5f5f5; transition: background .1s; }
tbody tr:last-child { border-bottom: none; }
tbody tr:hover { background: #fafafa; }
tbody td { padding: 12px 14px; vertical-align: middle; color: #1a1a1a; }
.td-muted { color: #6b7280; font-size: 13px; }

/* STATS GRID */
.stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 20px; }
.stat-card { background: var(--surface-panel); border: none; border-radius: var(--radius-module); padding: 20px; box-shadow: var(--shadow-module); }
.stat-label { font-size: 13px; font-weight: 500; color: #6b7280; margin-bottom: 8px; }
.stat-val { font-size: 26px; font-weight: 600; color: #1a1a1a; line-height: 1; margin-bottom: 4px; }
.stat-val.accent { color: var(--accent); }
.stat-sub { font-size: 13px; color: #9ca3af; }

/* DIVIDER */
.ch-divider { border: none; border-top: 1px solid #e5e5e5; margin: 20px 0; }

/* EMPTY */
.empty { text-align: center; padding: 48px 20px; color: #9ca3af; font-size: 15px; }
.empty-icon { font-size: 28px; opacity: .5; margin-bottom: 8px; }
.empty-icon-svg { display: flex; justify-content: center; margin: 0 auto 8px; color: var(--text2); opacity: 0.45; }
.empty-icon-svg svg { width: 32px; height: 32px; flex-shrink: 0; }
.empty-title { font-size: 15px; font-weight: 500; color: #6b7280; margin-bottom: 4px; }

/* MODAL */
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.4); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; }
.modal { background: var(--surface-panel); border-radius: var(--radius-module); padding: 28px; width: 100%; max-width: 480px; box-shadow: var(--shadow-module); }
.modal h3 { font-size: 17px; font-weight: 600; margin-bottom: 20px; }
.modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 24px; padding-top: 16px; border-top: 1px solid rgba(26,26,26,0.08); }

/* LOGIN — stessa schermata merchant */
.login-wrap {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  box-sizing: border-box;
  background-color: var(--canvas);
  background-image: url('https://carthub.cloud/wp-content/uploads/2026/03/svgviewer-output1.svg');
  background-position: center center;
  background-size: cover;
  background-repeat: no-repeat;
}
.login-card {
  width: 100%;
  max-width: 400px;
  background: var(--surface-panel);
  border: none;
  border-radius: var(--radius-module);
  padding: 40px 36px;
  box-shadow: var(--shadow-module);
}
.login-logo { font-size: 26px; font-weight: 700; letter-spacing: -0.03em; margin-bottom: 6px; color: #1a1a1a; line-height: 1.15; }
.login-logo span { color: var(--accent); }
.login-sub { font-size: 15px; color: #6b7280; margin-bottom: 28px; }
.login-label { display: block; font-size: 14px; font-weight: 500; color: #1a1a1a; margin-bottom: 6px; }
.login-input {
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  font-size: 15px;
  font-family: var(--font);
  border: 1px solid rgba(26, 26, 26, 0.08);
  border-radius: 12px;
  color: #1a1a1a;
  background: var(--surface-bright);
  outline: none;
  transition: border-color .15s, box-shadow .15s;
  margin-bottom: 14px;
}
.login-input:focus { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-glow); }
.login-btn {
  width: 100%;
  background: #1a1a1a;
  color: #fff;
  border: 1px solid #1a1a1a;
  border-radius: 8px;
  padding: 11px;
  font-family: var(--font);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background .15s, transform .1s;
  margin-top: 8px;
}
.login-btn:hover { background: #333; }
.login-btn:active { transform: scale(0.99); }
.login-btn:disabled { opacity: .5; cursor: not-allowed; }
.login-err { background: #fff5f5; border: 1px solid #ffcdd2; border-radius: 8px; color: #c62828; font-size: 13px; padding: 10px 12px; margin-top: 14px; line-height: 1.4; }
.login-ok { background: #f0faf6; border: 1px solid #b2dfdb; border-radius: 8px; color: #1b5e20; font-size: 13px; padding: 10px 12px; margin-top: 14px; line-height: 1.4; }
.login-lead { font-size: 14px; color: #4b5563; line-height: 1.55; margin: 0 0 20px; }
.login-link-btn {
  display: block;
  width: 100%;
  margin: 4px 0 10px;
  padding: 0;
  border: none;
  background: none;
  font-family: var(--font);
  font-size: 14px;
  font-weight: 500;
  color: var(--link);
  cursor: pointer;
  text-align: right;
  text-decoration: none;
}
.login-link-btn:hover { text-decoration: underline; color: var(--accent-hover); }
.login-link-btn--after { text-align: center; margin-top: 16px; }
.login-checkbox-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: -4px 0 14px;
  font-size: 13px;
  color: var(--text2);
  user-select: none;
}
.login-checkbox-row input { width: auto; margin: 0; cursor: pointer; accent-color: var(--accent); }
.login-checkbox-row label { cursor: pointer; font-weight: 400; color: var(--text2); }
.login-footer { margin-top: 22px; padding-top: 20px; border-top: 1px solid rgba(26, 26, 26, 0.08); text-align: center; }
.login-footer-muted { display: block; font-size: 13px; color: #9ca3af; margin-bottom: 8px; }
.login-footer-link {
  font-size: 14px;
  font-weight: 600;
  color: var(--link);
  text-decoration: none;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  font: inherit;
}
.login-footer-link:hover { text-decoration: underline; color: var(--accent-hover); }
.login-url-hint-bubble {
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.55;
  color: var(--text);
  background: var(--accent-bg);
  border: 1px solid var(--accent-border);
}

.subscription-plans-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

@media (max-width: 900px) {
  .mobile-nav-toggle { display: inline-flex; }
  .sidebar {
    position: fixed;
    left: var(--gap-module);
    top: var(--gap-module);
    bottom: var(--gap-module);
    z-index: 200;
    width: min(290px, calc(100vw - 16px));
    margin: 0;
    transform: translateX(calc(-100% - var(--gap-module)));
    transition: transform 0.22s ease;
    box-shadow: var(--shadow-module);
  }
  .sidebar.is-open {
    transform: translateX(0);
    box-shadow: var(--shadow-drawer);
  }
  .topbar { padding-left: 12px; padding-right: 12px; }
  .topbar-title { flex: 1 1 auto; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
}

@media (max-width: 1100px) { .stats-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 900px) { .subscription-plans-grid { grid-template-columns: 1fr; } }
@media (max-width: 600px) { .stats-grid { grid-template-columns: 1fr !important; } }
`;

// ─── HELPERS ────────────────────────────────────────
function av(n) { return (n||"?").split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2); }
function fmt(n) { return Number(n||0).toLocaleString("it-IT", {minimumFractionDigits:2,maximumFractionDigits:2}); }
function fmtDate(d) { if(!d) return "—"; return new Date(d).toLocaleDateString("it-IT"); }

// ─── SLOT BAR ────────────────────────────────────────
function SlotsBar({ agency, onUpgrade }) {
  if (!agency) return null;
  const { slots_used=0, slots_total=3, plan, trial_expires_at, cart_pool: cp } = agency;
  const pct = Math.min((slots_used / slots_total) * 100, 100);
  const fillClass = pct >= 100 ? "full" : pct >= 80 ? "warning" : "";
  const daysLeft = trial_expires_at
    ? Math.max(0, Math.ceil((new Date(trial_expires_at) - new Date()) / (1000*60*60*24)))
    : null;

  const poolPct = cp && cp.limit > 0 ? Math.min((cp.used / cp.limit) * 100, 100) : 0;
  const poolFill = poolPct >= 100 ? "full" : poolPct >= 80 ? "warning" : "";

  return (
    <div className="slots-stack">
      <div className="slots-bar">
        <span className="slots-label">Slot merchant</span>
        <span className="slots-count"><span>{slots_used}</span> / {slots_total}</span>
        <div className="slots-progress">
          <div className={`slots-fill${fillClass?" "+fillClass:""}`} style={{width:`${pct}%`}} />
        </div>
        {plan === "trial" && daysLeft !== null && (
          <>
            <span className="trial-badge">Trial — {daysLeft} giorni rimasti</span>
            <button className="ch-btn ch-btn-primary ch-btn-sm" onClick={onUpgrade}>Scegli piano →</button>
          </>
        )}
        {plan !== "trial" && plan !== "cancelled" && (
          <span className="ch-status success"><span className="status-dot"/>Piano {plan}</span>
        )}
        {plan === "cancelled" && (
          <button className="ch-btn ch-btn-primary ch-btn-sm" onClick={onUpgrade}>Riattiva →</button>
        )}
      </div>
      {cp && (
        <div className="slots-bar cart-pool-bar">
          <span className="slots-label">Pool carrelli (mese {cp.month_utc} UTC)</span>
          <span className="slots-count"><span>{cp.used}</span> / {cp.limit}</span>
          <div className="slots-progress">
            <div className={`slots-fill${poolFill?" "+poolFill:""}`} style={{width:`${poolPct}%`}} />
          </div>
          <span className="ch-status info" title="Tutti i merchant dell’agenzia condividono questo tetto mensile.">
            <span className="status-dot"/>Condiviso
          </span>
        </div>
      )}
    </div>
  );
}

// ─── BILLING ────────────────────────────────────────
function Billing({ agency, onPlanUpdate }) {
  const [plans, setPlans] = useState([]);
  const [interval, setIntervalType] = useState("yearly");
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    fetch("https://app.carthub.cloud/api/stripe/plans")
      .then(r => r.json())
      .then(d => { setPlans(d.plans || []); setLoading(false); });
  }, []);

  const startCheckout = async (priceId, planName) => {
    if (!isBillingComplete(agency)) {
      setMsg("Completa i dati di fatturazione nella sezione Account prima di procedere.");
      return;
    }
    setCheckoutLoading(priceId);
    setMsg("");
    const d = await api("/stripe/checkout", { method:"POST", body: JSON.stringify({ price_id: priceId }) });
    if (d.checkout_url) {
      window.location.href = d.checkout_url;
    } else {
      setMsg(d.error || "Errore nella creazione del checkout");
      setCheckoutLoading(null);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    const d = await api("/stripe/portal", { method:"POST" });
    if (d.portal_url) window.location.href = d.portal_url;
    else { setMsg(d.error || "Errore apertura portale"); setPortalLoading(false); }
  };

  const currentPlan = agency?.plan;
  const isActive = currentPlan && !["trial","cancelled"].includes(currentPlan);

  return (
    <div>
      {/* Banner checkout success */}
      {new URLSearchParams(window.location.search).get("checkout") === "success" && (
        <div className="ch-info" style={{background:"var(--green-bg)",border:"1px solid var(--green-border)",color:"var(--green)",marginBottom:20}}>
          <strong>Abbonamento attivato!</strong> Benvenuto nel piano CartHub Agency.
        </div>
      )}

      {msg && <div className="ch-err">{msg}</div>}

      {/* Gestione abbonamento attivo */}
      {isActive && (
        <div className="ch-card" style={{marginBottom:20}}>
          <div className="card-head">
            <h2>Il tuo abbonamento</h2>
            <span className="ch-status success"><span className="status-dot"/>Attivo</span>
          </div>
          <div style={{display:"flex",gap:24,flexWrap:"wrap",marginBottom:20}}>
            <div><div style={{fontSize:13,color:"#6b7280",marginBottom:4}}>Piano</div><div style={{fontWeight:600,textTransform:"capitalize"}}>{currentPlan}</div></div>
            <div><div style={{fontSize:13,color:"#6b7280",marginBottom:4}}>Fatturazione</div><div style={{fontWeight:600}}>{agency?.billing_interval === "yearly" ? "Annuale" : "Mensile"}</div></div>
            <div><div style={{fontSize:13,color:"#6b7280",marginBottom:4}}>Slot merchant</div><div style={{fontWeight:600}}>{agency?.slots_used} / {agency?.slots_total}</div></div>
            {agency?.current_period_end && <div><div style={{fontSize:13,color:"#6b7280",marginBottom:4}}>Prossimo rinnovo</div><div style={{fontWeight:600}}>{fmtDate(agency.current_period_end)}</div></div>}
          </div>
          <button className="ch-btn" onClick={openPortal} disabled={portalLoading}>
            {portalLoading ? "Apertura..." : "Gestisci abbonamento, fatture e pagamenti"}
          </button>
        </div>
      )}

      {/* Toggle mensile / annuale */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:12,marginBottom:28}}>
        <span style={{fontSize:15,fontWeight:500,color:interval==="monthly"?"#1a1a1a":"#9ca3af"}}>Mensile</span>
        <div
          onClick={()=>setIntervalType(interval==="monthly"?"yearly":"monthly")}
          style={{width:44,height:24,borderRadius:12,background:interval==="yearly"?"var(--accent)":"#e5e5e5",cursor:"pointer",position:"relative",transition:"background .2s"}}
        >
          <div style={{width:18,height:18,borderRadius:"50%",background:"#fff",position:"absolute",top:3,left:interval==="yearly"?23:3,transition:"left .2s",boxShadow:"0 1px 3px rgba(0,0,0,.2)"}}/>
        </div>
        <span style={{fontSize:15,fontWeight:500,color:interval==="yearly"?"#1a1a1a":"#9ca3af"}}>
          Annuale <span style={{fontSize:13,color:"var(--accent)",fontWeight:600}}>Risparmia fino al 16%</span>
        </span>
      </div>

      {/* Cards piani */}
      {loading ? (
        <div className="empty"><div className="empty-title">Caricamento piani…</div></div>
      ) : (
        <div className="subscription-plans-grid">
          {plans.map(plan => {
            const priceId = interval === "yearly" ? plan.price_id_yearly : plan.price_id_monthly;
            const price = interval === "yearly" ? plan.price_yearly : plan.price_monthly;
            const isCurrent = currentPlan === plan.key;
            const isPopular = plan.popular;
            return (
              <div key={plan.key} style={{background:"var(--surface-panel)",border:`2px solid ${isPopular?"var(--accent)":"#e5e5e5"}`,borderRadius:18,padding:24,position:"relative",display:"flex",flexDirection:"column",boxShadow:"var(--shadow-module)"}}>
                {isPopular && <div style={{position:"absolute",top:-12,left:"50%",transform:"translateX(-50%)",background:"var(--accent)",color:"#fff",fontSize:12,fontWeight:600,padding:"3px 12px",borderRadius:20,whiteSpace:"nowrap"}}>Più scelto</div>}
                <div style={{fontSize:17,fontWeight:600,marginBottom:4}}>{plan.name}</div>
                <div style={{fontSize:13,color:"#6b7280",marginBottom:16}}>{plan.slots} shop WooCommerce</div>
                <div style={{marginBottom:4}}>
                  <span style={{fontSize:32,fontWeight:700}}>€{price}</span>
                  <span style={{fontSize:15,color:"#6b7280"}}>/{interval==="yearly"?"anno":"mese"}</span>
                </div>
                {interval==="yearly" && (
                  <div style={{fontSize:13,color:"var(--accent)",fontWeight:500,marginBottom:16}}>Risparmia {plan.savings_yearly}% vs mensile</div>
                )}
                {interval==="monthly" && <div style={{marginBottom:16}}/>}
                <ul style={{listStyle:"none",padding:0,margin:"0 0 20px 0",flex:1}}>
                  {plan.features.map((f,i)=>(
                    <li key={i} style={{fontSize:14,color:"#374151",padding:"4px 0",display:"flex",alignItems:"center",gap:8}}>
                      <span style={{color:"var(--green)",fontWeight:600}}>✓</span>{f}
                    </li>
                  ))}
                </ul>
                {isCurrent ? (
                  <button className="ch-btn" disabled style={{width:"100%",justifyContent:"center",opacity:1,background:"#f0faf0",border:"1px solid #c8e6c9",color:"#2e7d32"}}>
                    ✓ Piano attuale
                  </button>
                ) : (
                  <button
                    className="ch-btn ch-btn-primary"
                    style={{width:"100%",justifyContent:"center",background:isPopular?"var(--accent)":"#1a1a1a",borderColor:isPopular?"var(--accent)":"#1a1a1a"}}
                    onClick={()=>startCheckout(priceId, plan.name)}
                    disabled={checkoutLoading===priceId}
                  >
                    {checkoutLoading===priceId ? "Reindirizzamento..." : isActive ? "Cambia piano" : "Inizia ora"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div style={{textAlign:"center",marginTop:20,fontSize:13,color:"#9ca3af"}}>
        Pagamenti sicuri tramite Stripe · Cancella in qualsiasi momento · Fattura IVA disponibile
      </div>
    </div>
  );
}

// ─── MODAL AGGIUNGI MERCHANT ─────────────────────────
function AddMerchantModal({ onClose, onSuccess, slotsAvailable }) {
  const [form, setForm] = useState({ shop_name:"", shop_domain:"", email:"", password:"", language:"it" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const submit = async () => {
    if (!form.shop_name || !form.shop_domain || !form.email || !form.password) {
      return setErr("Tutti i campi sono obbligatori");
    }
    setLoading(true); setErr("");
    const d = await api("/agency/merchants", { method:"POST", body:JSON.stringify(form) });
    setLoading(false);
    if (d.success) { onSuccess(); onClose(); }
    else setErr(d.error || "Errore durante la creazione");
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal">
        <h3>Aggiungi merchant</h3>
        {!slotsAvailable && (
          <div className="ch-err">Slot esauriti — fai upgrade del piano per aggiungere altri merchant.</div>
        )}
        {err && <div className="ch-err">{err}</div>}
        <div className="ch-field">
          <label>Nome shop</label>
          <input className="ch-input" value={form.shop_name} onChange={e=>set("shop_name",e.target.value)} placeholder="Es. Boutique Milano" />
        </div>
        <div className="ch-field">
          <label>Dominio WooCommerce</label>
          <input className="ch-input" value={form.shop_domain} onChange={e=>set("shop_domain",e.target.value)} placeholder="shop.example.com" />
          <div className="ch-hint">Solo il dominio, senza https://</div>
        </div>
        <div className="ch-field">
          <label>Email merchant</label>
          <input className="ch-input" type="email" value={form.email} onChange={e=>set("email",e.target.value)} placeholder="merchant@example.com" />
        </div>
        <div className="ch-field">
          <label>Password accesso dashboard</label>
          <input className="ch-input" type="password" value={form.password} onChange={e=>set("password",e.target.value)} placeholder="••••••••" />
        </div>
        <div className="ch-field">
          <label>Lingua email</label>
          <select className="ch-select" value={form.language} onChange={e=>set("language",e.target.value)}>
            <option value="it">Italiano</option>
            <option value="en">English</option>
            <option value="es">Español</option>
            <option value="de">Deutsch</option>
            <option value="fr">Français</option>
          </select>
        </div>
        <div className="modal-actions">
          <button className="ch-btn" onClick={onClose}>Annulla</button>
          <button className="ch-btn ch-btn-primary" onClick={submit} disabled={loading||!slotsAvailable}>
            {loading ? "Creazione..." : "Crea merchant"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── PAGINA MERCHANTS ─────────────────────────────────
function Merchants({ agency, onSlotsUpdate }) {
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState("");

  const load = () => {
    setLoading(true);
    api("/agency/merchants").then(d => {
      setMerchants(d.shops || d.merchants || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => {
    load();
    onSlotsUpdate?.();
    // Intenzionale: refresh pool/slot agenzia all’apertura lista merchant (onSlotsUpdate = refreshAgency dal parent).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleImpersonate = async (merchantId) => {
    const d = await api(`/agency/merchants/${merchantId}/impersonate`, { method:"POST" });
    if (d.token) {
      // Apre dashboard merchant in nuova tab con JWT temporaneo
      const url = `${DASHBOARD_URL}?impersonate=${d.token}&shop=${encodeURIComponent(JSON.stringify(d.shop))}`;
      window.open(url, "_blank");
    }
  };

  const filtered = merchants.filter(m =>
    !search || m.name?.toLowerCase().includes(search.toLowerCase()) || m.domain?.toLowerCase().includes(search.toLowerCase())
  );

  const poolByShop = Object.fromEntries(
    (agency?.cart_pool?.by_shop || []).map((r) => [r.shop_id, r.carts_in_month])
  );

  const slotsAvailable = (agency?.slots_used || 0) < (agency?.slots_total || 3);

  return (
    <div>
      <div className="ch-card">
        <div className="card-head">
          <h2>Merchant ({merchants.length})</h2>
          <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
            <input className="ch-input full" style={{maxWidth:280,minWidth:0,flex:"1 1 160px"}} placeholder="Cerca per nome o dominio…" value={search} onChange={e=>setSearch(e.target.value)} />
            <button className="ch-btn ch-btn-primary" onClick={()=>setShowAdd(true)}>
              + Aggiungi merchant
            </button>
          </div>
        </div>

        {loading ? (
          <div className="empty"><div className="empty-title">Caricamento…</div></div>
        ) : filtered.length === 0 ? (
          <div className="empty">
            <div className="empty-icon-svg" aria-hidden>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
            </div>
            <div className="empty-title">{search ? "Nessun merchant trovato" : "Nessun merchant ancora"}</div>
            {!search && <div style={{fontSize:13,color:"#9ca3af",marginTop:4}}>Aggiungi il primo merchant per iniziare</div>}
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Shop</th>
                  <th>Recovery rate</th>
                  <th>Pool mese</th>
                  <th>Revenue recuperato</th>
                  <th>Email</th>
                  <th>Stato</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(m => (
                  <tr key={m.id}>
                    <td>
                      <div style={{fontWeight:500}}>{m.name}</div>
                      <div className="td-muted">{m.domain}</div>
                    </td>
                    <td>
                      <span style={{fontWeight:600,color: Number(m.recovery_rate)>=30?"var(--green)":Number(m.recovery_rate)>=15?"var(--yellow)":"var(--text)"}}>
                        {m.recovery_rate || 0}%
                      </span>
                      <div className="td-muted">{m.total_carts || 0} carrelli (30gg)</div>
                    </td>
                    <td>
                      <span style={{fontWeight:600}}>{poolByShop[m.id] ?? 0}</span>
                      <div className="td-muted">nel pool UTC</div>
                    </td>
                    <td style={{fontWeight:500}}>€{fmt(m.revenue_recovered)}</td>
                    <td>
                      <span className={`ch-status ${m.email_tier==="pro_active"?"success":"neutral"}`}>
                        <span className="status-dot"/>
                        {m.email_tier==="pro_active" ? "Pro Tier" : "Quick Start"}
                      </span>
                    </td>
                    <td>
                      <span className={`ch-status ${m.active?"success":"error"}`}>
                        <span className="status-dot"/>
                        {m.active ? "Attivo" : "Inattivo"}
                      </span>
                    </td>
                    <td>
                      <button className="ch-btn ch-btn-sm" onClick={()=>handleImpersonate(m.id)}>
                        Apri dashboard →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAdd && (
        <AddMerchantModal
          slotsAvailable={slotsAvailable}
          onClose={() => setShowAdd(false)}
          onSuccess={() => { load(); if(onSlotsUpdate) onSlotsUpdate(); }}
        />
      )}
    </div>
  );
}

// ─── PAGINA IMPOSTAZIONI ──────────────────────────────
function isBillingComplete(a) {
  return !!(a?.billing_name && a?.billing_address && a?.billing_city && a?.billing_zip && a?.billing_country && a?.vat_number);
}

function Settings({ agency, onUpdate }) {
  const [billing, setBilling] = useState({
    billing_name:    agency?.billing_name    || "",
    billing_address: agency?.billing_address || "",
    billing_city:    agency?.billing_city    || "",
    billing_zip:     agency?.billing_zip     || "",
    billing_country: agency?.billing_country || "IT",
    vat_number:      agency?.vat_number      || "",
  });
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);
  const [err,    setErr]    = useState("");

  const complete = isBillingComplete(billing);

  const save = async () => {
    setSaving(true); setErr(""); setSaved(false);
    const d = await api("/agency/me", { method:"PUT", body:JSON.stringify(billing) });
    if (d.success) {
      setSaved(true);
      if (onUpdate) onUpdate(d.agency);
      setTimeout(() => setSaved(false), 3000);
    } else {
      setErr(d.error || "Errore nel salvataggio.");
    }
    setSaving(false);
  };

  const f = field => ({
    value: billing[field],
    onChange: e => setBilling(p => ({ ...p, [field]: e.target.value }))
  });

  return (
    <div>
      {/* Dati account */}
      <div className="ch-card">
        <div style={{fontWeight:600,fontSize:15,color:"#1a1a1a",marginBottom:16}}>Account agenzia</div>
        <hr className="ch-divider" />
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:15}}>
          <tbody>
            {[
              ["Nome agenzia", agency?.name || "—"],
              ["Email",        agency?.email || "—"],
              ["Sito web",     agency?.website || "—"],
              ["Piano",        agency?.plan || "—"],
              ["Slot totali",  agency?.slots_total || agency?.max_shops || 3],
              ["Slot usati",   agency?.slots_used || 0],
              ["Trial scade",  fmtDate(agency?.trial_expires_at || agency?.trial_ends_at)],
            ].map(([k,v]) => (
              <tr key={k} style={{borderBottom:"1px solid #f5f5f5"}}>
                <td style={{padding:"10px 0",color:"#6b7280",width:180,fontSize:14}}>{k}</td>
                <td style={{padding:"10px 0",fontWeight:500,fontSize:14}}>{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dati fiscali */}
      <div className="ch-card" style={{marginTop:20}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
          <div>
            <div style={{fontWeight:600,fontSize:15,color:"#1a1a1a"}}>Dati di fatturazione</div>
            <div style={{fontSize:13,color:"#6b7280",marginTop:2}}>Necessari per attivare un piano a pagamento</div>
          </div>
          {complete
            ? <span style={{background:"#f0fdf4",color:"#166534",border:"1px solid #bbf7d0",borderRadius:20,padding:"3px 12px",fontSize:12,fontWeight:600}}>Completi</span>
            : <span style={{background:"#fffbeb",color:"#92400e",border:"1px solid #fde68a",borderRadius:20,padding:"3px 12px",fontSize:12,fontWeight:600}}>Incompleti</span>
          }
        </div>
        <hr className="ch-divider" />

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"12px 16px",marginTop:16}}>
          <div className="ch-field" style={{gridColumn:"1/-1"}}>
            <label>Ragione sociale / Nome completo</label>
            <input className="ch-input" placeholder="es. Agenzia Web Roma Srl" {...f("billing_name")} />
          </div>
          <div className="ch-field" style={{gridColumn:"1/-1"}}>
            <label>Indirizzo</label>
            <input className="ch-input" placeholder="Via Roma 1" {...f("billing_address")} />
          </div>
          <div className="ch-field">
            <label>Città</label>
            <input className="ch-input" placeholder="Roma" {...f("billing_city")} />
          </div>
          <div className="ch-field">
            <label>CAP</label>
            <input className="ch-input" placeholder="00100" {...f("billing_zip")} />
          </div>
          <div className="ch-field">
            <label>Paese</label>
            <select className="ch-input" {...f("billing_country")} style={{background:"#fff"}}>
              <option value="IT">🇮🇹 Italia</option>
              <option value="DE">🇩🇪 Germania</option>
              <option value="FR">🇫🇷 Francia</option>
              <option value="ES">🇪🇸 Spagna</option>
              <option value="GB">🇬🇧 Regno Unito</option>
              <option value="US">🇺🇸 Stati Uniti</option>
              <option value="CH">🇨🇭 Svizzera</option>
              <option value="AT">🇦🇹 Austria</option>
              <option value="BE">🇧🇪 Belgio</option>
              <option value="NL">🇳🇱 Paesi Bassi</option>
              <option value="PT">🇵🇹 Portogallo</option>
              <option value="PL">🇵🇱 Polonia</option>
              <option value="OTHER">Altro</option>
            </select>
          </div>
          <div className="ch-field">
            <label>Partita IVA / VAT number</label>
            <input className="ch-input" placeholder="IT12345678901" {...f("vat_number")} />
          </div>
        </div>

        {err   && <div style={{marginTop:12,color:"#c62828",fontSize:13}}>{err}</div>}
        {saved && <div style={{marginTop:12,color:"#166534",fontSize:13}}>✓ Dati salvati correttamente</div>}

        <div style={{marginTop:20}}>
          <button className="ch-btn ch-btn-primary" onClick={save} disabled={saving}>
            {saving ? "Salvataggio…" : "Salva dati di fatturazione"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── LOGIN ────────────────────────────────────────────
const AGENCY_REGISTER_URL_HINT =
  "Se nel campo c'è già qualcosa ma compare ancora questo messaggio, di solito manca proprio il prefisso https:// all'inizio (non basta www. da solo). Confronta con: https://www.tuosito.it — puoi anche uscire dal campo: aggiungiamo https:// automaticamente se manca.";

function normalizeAgencyWebsite(val) {
  const trimmed = (val || "").trim();
  if (!trimmed) return { ok: false, website: "" };
  let full = trimmed;
  if (!/^https?:\/\//i.test(full)) {
    full = `https://${full.replace(/^\/+/, "")}`;
  }
  try {
    const u = new URL(full);
    const h = u.hostname.toLowerCase();
    if (!h || !h.includes(".")) return { ok: false, website: full };
    return { ok: true, website: full };
  } catch {
    return { ok: false, website: full };
  }
}

function Login({ onLogin }) {
  const [mode, setMode] = useState("login"); // login | register | register_done | forgot | reset
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [loading, setLoading] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [agencyName, setAgencyName] = useState("");
  const [website, setWebsite] = useState("");
  const [registerDoneEmail, setRegisterDoneEmail] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [registerUrlExplain, setRegisterUrlExplain] = useState(false);

  const pwType = showPw ? "text" : "password";

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("reset_token");
    if (t && t.length >= 20) {
      setResetToken(t);
      setMode("reset");
      window.history.replaceState({}, "", window.location.pathname || "/");
      return;
    }
    if (params.get("register") === "1") {
      setMode("register");
      window.history.replaceState({}, "", window.location.pathname || "/");
    }
  }, []);

  const ensureWebsiteHttpsPrefix = () => {
    const v = website.trim();
    if (!v) return;
    if (!/^https?:\/\//i.test(v)) {
      setWebsite(`https://${v.replace(/^\/+/, "")}`);
    }
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    if (blocked) return;
    setLoading(true);
    setErr("");
    setOk("");
    try {
      const d = await api("/agency/login", { method: "POST", body: JSON.stringify({ email, password: pw }) });
      if (d.token) {
        localStorage.setItem("carthub_agency_token", d.token);
        localStorage.setItem("carthub_agency", JSON.stringify(d.agency));
        onLogin(d.agency);
      } else if (d.error && d.error.includes("Troppi tentativi")) {
        setBlocked(true);
        setErr(d.error);
        setTimeout(() => { setBlocked(false); setErr(""); }, 15 * 60 * 1000);
      } else {
        setErr(d.error || "Credenziali non valide");
      }
    } catch {
      setErr("Errore di connessione al server");
    }
    setLoading(false);
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    setErr("");
    setOk("");
    setRegisterUrlExplain(false);
    if (!agencyName.trim() || !email.trim() || !website.trim() || !pw || !pw2) {
      setErr("Compila tutti i campi.");
      return;
    }
    if (pw.length < 8) {
      setErr("La password deve essere di almeno 8 caratteri.");
      return;
    }
    if (pw !== pw2) {
      setErr("Le password non coincidono.");
      return;
    }
    let w = website.trim();
    if (w && !/^https?:\/\//i.test(w)) {
      w = `https://${w.replace(/^\/+/, "")}`;
      setWebsite(w);
    }
    const norm = normalizeAgencyWebsite(w);
    if (!norm.ok) {
      setErr("Inserisci l'URL nel formato https:// (es. https://www.tuosito.it).");
      setRegisterUrlExplain(true);
      return;
    }
    setLoading(true);
    try {
      const d = await api("/agency/register", {
        method: "POST",
        body: JSON.stringify({
          name: agencyName.trim(),
          email: email.trim().toLowerCase(),
          password: pw,
          website: norm.website,
        }),
      });
      if (d.success) {
        setRegisterDoneEmail(email.trim());
        setAgencyName("");
        setEmail("");
        setWebsite("");
        setPw("");
        setPw2("");
        setShowPw(false);
        setMode("register_done");
      } else {
        setErr(d.error || "Registrazione non riuscita. Riprova.");
      }
    } catch {
      setErr("Errore di connessione al server");
    }
    setLoading(false);
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    setOk("");
    try {
      const d = await api("/agency/forgot-password", { method: "POST", body: JSON.stringify({ email }) });
      if (d.error && !d.success) {
        setErr(d.error);
      } else {
        setOk(d.message || "Controlla la tua casella email.");
        setEmail("");
      }
    } catch {
      setErr("Errore di connessione al server");
    }
    setLoading(false);
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (pw.length < 8) {
      setErr("La password deve essere di almeno 8 caratteri.");
      return;
    }
    if (pw !== pw2) {
      setErr("Le password non coincidono.");
      return;
    }
    setLoading(true);
    setErr("");
    setOk("");
    try {
      const d = await api("/agency/reset-password", {
        method: "POST",
        body: JSON.stringify({ token: resetToken, password: pw }),
      });
      if (d.error) {
        setErr(d.error);
      } else {
        setOk(d.message || "Password aggiornata.");
        setPw("");
        setPw2("");
        setResetToken("");
        setMode("login");
      }
    } catch {
      setErr("Errore di connessione al server");
    }
    setLoading(false);
  };

  return (
    <div className="login-wrap">
      <div className="login-card">
        <div className="login-logo" aria-label="CartHub">
          Cart<span>Hub</span>
        </div>
        <div className="login-sub">Pannello di controllo agenzia</div>

        {mode === "login" && (
          <form onSubmit={submitLogin}>
            <label className="login-label">Email</label>
            <input
              className="login-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="agenzia@example.com"
              autoComplete="username"
              required
            />
            <label className="login-label">Password</label>
            <input
              className="login-input"
              type={pwType}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              required
            />
            <div className="login-checkbox-row">
              <input
                type="checkbox"
                id="agency-login-show-pw"
                checked={showPw}
                onChange={(e) => setShowPw(e.target.checked)}
              />
              <label htmlFor="agency-login-show-pw">Mostra password</label>
            </div>
            <button
              type="button"
              className="login-link-btn"
              onClick={() => { setMode("forgot"); setErr(""); setOk(""); }}
            >
              Password dimenticata?
            </button>
            <button className="login-btn" type="submit" disabled={loading || blocked}>
              {loading ? "Accesso in corso…" : blocked ? "Accesso bloccato" : "Accedi"}
            </button>
            {err && <div className="login-err">{err}</div>}
            {ok && <div className="login-ok">{ok}</div>}
            <div className="login-footer">
              <span className="login-footer-muted">Non hai ancora un account agenzia?</span>
              <button
                type="button"
                className="login-footer-link"
                onClick={() => {
                  setMode("register");
                  setErr("");
                  setOk("");
                  setPw("");
                  setShowPw(false);
                  setRegisterUrlExplain(false);
                }}
              >
                Registrati come agenzia
              </button>
            </div>
          </form>
        )}

        {mode === "register" && (
          <form onSubmit={submitRegister}>
            <p className="login-lead">Richiedi accesso al pannello agenzia. Dopo la verifica email il team approva l&apos;account (solitamente entro 24 ore).</p>
            <label className="login-label">Nome agenzia</label>
            <input
              className="login-input"
              type="text"
              value={agencyName}
              onChange={(e) => setAgencyName(e.target.value)}
              placeholder="Es. Digital Marketing SRL"
              autoComplete="organization"
              required
            />
            <label className="login-label">Email</label>
            <input
              className="login-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="agenzia@example.com"
              autoComplete="email"
              required
            />
            <label className="login-label">Sito web (vetrina)</label>
            <input
              className="login-input"
              type="text"
              inputMode="url"
              value={website}
              onChange={(e) => {
                setRegisterUrlExplain(false);
                setWebsite(e.target.value);
              }}
              onBlur={ensureWebsiteHttpsPrefix}
              placeholder="https://www.tuosito.it"
              autoComplete="url"
              required
            />
            <p className="login-lead" style={{ fontSize: 12, color: "#6b7280", marginTop: -8 }}>
              Inserisci l&apos;URL nel formato https:// (es. https://www.tuosito.it); salveremo l&apos;indirizzo completo per contattarti.
            </p>
            <label className="login-label">Password</label>
            <input
              className="login-input"
              type={pwType}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="Minimo 8 caratteri"
              autoComplete="new-password"
              minLength={8}
              required
            />
            <label className="login-label">Conferma password</label>
            <input
              className="login-input"
              type={pwType}
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              placeholder="Ripeti la password"
              autoComplete="new-password"
              minLength={8}
              required
            />
            <div className="login-checkbox-row">
              <input
                type="checkbox"
                id="agency-reg-show-pw"
                checked={showPw}
                onChange={(e) => setShowPw(e.target.checked)}
              />
              <label htmlFor="agency-reg-show-pw">Mostra password</label>
            </div>
            <button className="login-btn" type="submit" disabled={loading}>
              {loading ? "Registrazione in corso…" : "Invia richiesta di registrazione"}
            </button>
            <button
              type="button"
              className="login-link-btn login-link-btn--after"
              onClick={() => {
                setMode("login");
                setErr("");
                setOk("");
                setAgencyName("");
                setWebsite("");
                setPw("");
                setPw2("");
                setShowPw(false);
                setRegisterUrlExplain(false);
              }}
            >
              Torna al login
            </button>
            {err && <div className="login-err">{err}</div>}
            {registerUrlExplain && (
              <div className="login-url-hint-bubble" role="note">
                {AGENCY_REGISTER_URL_HINT}
              </div>
            )}
          </form>
        )}

        {mode === "register_done" && (
          <div>
            <p className="login-lead" style={{ textAlign: "center", fontWeight: 600 }}>
              Controlla la tua email
            </p>
            <p className="login-lead" style={{ textAlign: "center" }}>
              Abbiamo inviato un link di verifica a <strong>{registerDoneEmail}</strong>. Dopo aver cliccato il link, il team CartHub riceverà la richiesta di approvazione.
            </p>
            <button
              type="button"
              className="login-btn"
              onClick={() => {
                setMode("login");
                setRegisterDoneEmail("");
                setErr("");
                setOk("");
              }}
            >
              Torna al login
            </button>
          </div>
        )}

        {mode === "forgot" && (
          <form onSubmit={submitForgot}>
            <p className="login-lead">
              Inserisci l&apos;email del tuo account agenzia. Se è registrata, riceverai un link per
              reimpostare la password (valido 1 ora).
            </p>
            <label className="login-label">Email</label>
            <input
              className="login-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="agenzia@example.com"
              autoComplete="email"
              required
            />
            <button className="login-btn" type="submit" disabled={loading}>
              {loading ? "Invio in corso…" : "Invia istruzioni"}
            </button>
            <button
              type="button"
              className="login-link-btn login-link-btn--after"
              onClick={() => { setMode("login"); setErr(""); setOk(""); }}
            >
              Torna al login
            </button>
            {err && <div className="login-err">{err}</div>}
            {ok && <div className="login-ok">{ok}</div>}
          </form>
        )}

        {mode === "reset" && (
          <form onSubmit={submitReset}>
            <p className="login-lead">Scegli una nuova password per il pannello agenzia (minimo 8 caratteri).</p>
            <label className="login-label">Nuova password</label>
            <input
              className="login-input"
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="••••••••"
              autoComplete="new-password"
              required
            />
            <label className="login-label">Ripeti password</label>
            <input
              className="login-input"
              type="password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              placeholder="••••••••"
              autoComplete="new-password"
              required
            />
            <button className="login-btn" type="submit" disabled={loading}>
              {loading ? "Salvataggio…" : "Imposta password"}
            </button>
            <button
              type="button"
              className="login-link-btn login-link-btn--after"
              onClick={() => { setMode("login"); setErr(""); setOk(""); setPw(""); setPw2(""); setResetToken(""); }}
            >
              Torna al login
            </button>
            {err && <div className="login-err">{err}</div>}
            {ok && <div className="login-ok">{ok}</div>}
          </form>
        )}
      </div>
    </div>
  );
}

// ─── APP ──────────────────────────────────────────────
const TABS = [
  { id:"merchants", label:"Merchant", icon:<svg viewBox="0 0 20 20" fill="currentColor"><path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/></svg> },
  { id:"billing",   label:"Abbonamento", icon:<svg viewBox="0 0 20 20" fill="currentColor"><path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"/><path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd"/></svg> },
  { id:"settings",  label:"Account",  icon:<svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd"/></svg> },
];

const IcMenu = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
    <line x1="4" y1="6" x2="20" y2="6" /><line x1="4" y1="12" x2="20" y2="12" /><line x1="4" y1="18" x2="20" y2="18" />
  </svg>
);

export default function App() {
  const [auth, setAuth] = useState(() => !!localStorage.getItem("carthub_agency_token"));
  const [agency, setAgency] = useState(() => { try { return JSON.parse(localStorage.getItem("carthub_agency")); } catch { return null; } });
  const [tab, setTab] = useState("merchants");
  const [refreshing, setRefreshing] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  const refreshAgency = async () => {
    const d = await api("/agency/me");
    if (d.id) { setAgency(d); localStorage.setItem("carthub_agency", JSON.stringify(d)); }
  };

  // Gestisce redirect da Stripe checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") === "success") {
      window.history.replaceState({}, "", "/");
      setTab("billing");
      setRefreshing(true);
      refreshAgency().finally(() => setRefreshing(false));
    }
    if (params.get("checkout") === "cancelled") {
      window.history.replaceState({}, "", "/");
      setTab("billing");
    }
  }, []);

  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setNavOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navOpen]);

  useEffect(() => {
    const onResize = () => { if (window.innerWidth > 900) setNavOpen(false); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handleLogin = (agencyData) => {
    setAgency(agencyData);
    setAuth(true);
  };

  const logout = () => {
    localStorage.removeItem("carthub_agency_token");
    localStorage.removeItem("carthub_agency");
    setAuth(false); setAgency(null);
  };

  if (!auth) return <><style>{styles}</style><Login onLogin={handleLogin}/></>;

  if (refreshing) return (
    <>
      <style>{styles}</style>
      <div className="app-loading-screen">
        <div className="app-loading-spinner" aria-hidden />
        <div>Aggiornamento piano in corso…</div>
      </div>
    </>
  );

  const titles = { merchants:"Merchant", billing:"Abbonamento", settings:"Account" };

  return (
    <>
      <style>{styles}</style>
      <div className="app">
        <div
          className={`sidebar-backdrop${navOpen ? " is-open" : ""}`}
          onClick={() => setNavOpen(false)}
          aria-hidden={!navOpen}
        />
        <aside className={`sidebar${navOpen ? " is-open" : ""}`}>
          <div className="sidebar-head">
            <div className="sidebar-logo" aria-label="CartHub">Cart<span>Hub</span></div>
            <div className="sidebar-sub">Pannello agenzia</div>
          </div>
          <nav className="sidebar-nav">
            {TABS.map(t => (
              <div
                key={t.id}
                className={`nav-item${tab === t.id ? " active" : ""}`}
                onClick={() => { setTab(t.id); setNavOpen(false); }}
              >
                {t.icon}{t.label}
                {t.id === "merchants" && agency?.slots_used > 0 && (
                  <span className="nav-badge">{agency.slots_used}</span>
                )}
              </div>
            ))}
          </nav>
          <div className="sidebar-foot">
            <div className="agency-row">
              <div className="agency-av">{av(agency?.name)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="agency-name">{agency?.name || "Agenzia"}</div>
                <div className="agency-email">{agency?.email}</div>
              </div>
              <button type="button" className="logout-btn" onClick={logout} title="Logout">
                <svg width="15" height="15" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                  <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 001 1h7a1 1 0 100-2H4V5h6a1 1 0 100-2H3zm10.293 4.293a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 01-1.414-1.414L14.586 11H7a1 1 0 110-2h7.586l-1.293-1.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </aside>

        <div className="main">
          <div className="topbar">
            <button
              type="button"
              className="mobile-nav-toggle"
              aria-label="Apri menu di navigazione"
              aria-expanded={navOpen}
              onClick={() => setNavOpen((o) => !o)}
            >
              {IcMenu}
            </button>
            <div className="topbar-title">{titles[tab]}</div>
          </div>
          <div className="content">
            <SlotsBar agency={agency} onUpgrade={() => { setTab("billing"); setNavOpen(false); }} />
            {tab === "merchants" && <Merchants agency={agency} onSlotsUpdate={refreshAgency} />}
            {tab === "billing" && <Billing agency={agency} onPlanUpdate={refreshAgency} />}
            {tab === "settings" && <Settings agency={agency} onUpdate={a => { setAgency(a); localStorage.setItem("carthub_agency", JSON.stringify(a)); }} />}
          </div>
        </div>
      </div>
    </>
  );
}
